import React, { useState, useEffect, useRef, useContext, useMemo, Fragment } from "react";
import { useRouter } from "next/router";
import { AppContext } from "../../../context/state";
import SearchInput from "../../../components/search-input/SearchInput";
import Content from "../../../components/layout/Content";
import { TableHeader } from "../../../nextJSComponents/TableHeader";
import TableSkeleton from "../../../components/table/TableSkeleton";
import useWindowResize from "../../../components/hooks/useWindowResize";
import useInfiniteScroll from "../../../components/hooks/useInfiniteScroll";
import ScrollableTable from "../../../components/table/ScrollableTable";
import TableCellRegressionDiff from "../../../components/table/TableCellRegressionDiff";
import useDynamicSort from "../../../components/hooks/useDynamicSort";
import LoadingSkeleton from "../../../components/animations/LoadingSkeleton";
import FilterByCoachConf from "../../../components/button/FilterByCoachConf";
import Utils from "../../../utils/Utils";
import { useCookies } from "react-cookie";
import { authGetRequest } from "../../../utils/authGetRequest";
import Head from "next/head";
import MainLayout from "../../../components/MainLayout/MainLayout";
import Link from "next/link";
import SQBestBetsSreenshot from "../../../assets/images/SQBestBetsSreenshot.png";
import { Form, InputGroup, Button } from "react-bootstrap";
import { appServerSideProps } from "../../../utils/getAppService";
import { getDefYear, getYearArray } from "../../../constants/main";
import { SIGNUPS_CLOSED_MESSAGE } from "../../../constants/main";
const regressionTableHeader = [
  {
    title: "Team",
    showImage: false,
    sortable: true,
    key: "Logos",
    classList: ["hard_left"],
  },
  {
    key: "Conference",
    title: "Conference",
    "displayIn%": false,
    classList: ["next_left"],
  },
  {
    title: "Finishing at the Rim",
    key: "Finishing at the Rim",
    keyDiff: "Finishing at the Rim Diff",
    keyDiffRank: "Finishing at the Rim Diff rank",
    keyFG: "Finishing at the Rim FG%",
    keyFGRank: "Finishing at the Rim FG% rank",
    keySQ: "Finishing at the Rim SQ FG%",
    keySQRank: "Finishing at the Rim SQ FG% rank",
    classList: ["next_left"],
    sortable: true,
  },
  {
    title: "3PT",
    key: "3PT",
    keyDiff: "3PT Diff",
    keyDiffRank: "3PT Diff rank",
    keyFG: "3PT FG%",
    keyFGRank: "3PT FG% rank",
    keySQ: "3PT SQ FG%",
    keySQRank: "3PT SQ FG% rank",
    sortable: true,
  },
  {
    title: "Midrange",
    key: "Midrange",
    keyDiff: "Midrange Diff",
    keyDiffRank: "Midrange Diff rank",
    keyFG: "Midrange FG%",
    keyFGRank: "Midrange FG% rank",
    keySQ: "Midrange SQ FG%",
    keySQRank: "Midrange SQ FG% rank",
    sortable: true,
  },
  {
    title: "Post-Up",
    key: "Post-Up",
    keyDiff: "Post-Up Diff",
    keyDiffRank: "Post-Up Diff rank",
    keyFG: "Post-Up FG%",
    keyFGRank: "Post-Up FG% rank",
    keySQ: "Post-Up SQ FG%",
    keySQRank: "Post-Up SQ FG% rank",
    sortable: true,
  },
  {
    title: "Free Throws",
    key: "Free Throws",
    keyDiff: "Free Throws Diff",
    keyDiffRank: "Free Throws Diff rank",
    keyFG: "Free Throw FG%",
    keyFGRank: "Free Throw FG% rank",
    keySQ: "Free Throw SQ FG%",
    keySQRank: "Free Throw SQ FG% rank",
    sortable: true,
  },
];

const getDynamicRanks = (arr = [], tableType) => {
  const getTeamRank = (teamName, sortedArr) => {
    return sortedArr.findIndex((elem) => elem["Team"] === teamName) + 1;
  };
  const sortOrder = tableType === "regression_def" ? "asc" : "desc";

  const sortedTeamsRank = {};

  regressionTableHeader.forEach((tableColumn, index) => {
    if (index > 1) {
      sortedTeamsRank[tableColumn["keyFGRank"]] = [...arr.sort(Utils.dynamicsort(tableColumn["keyFG"], sortOrder))].map(
        (elem, index) => {
          return {
            ...elem,
            [tableColumn["keyFGRank"]]: index + 1,
          };
        }
      );
      sortedTeamsRank[tableColumn["keySQRank"]] = [...arr.sort(Utils.dynamicsort(tableColumn["keySQ"], sortOrder))].map(
        (elem, index) => {
          return {
            ...elem,
            [tableColumn["keySQRank"]]: index + 1,
          };
        }
      );
    }
  });

  let sortedAndRankedResponse = arr.sort(Utils.dynamicsort("Adjusted_Shot_Quality", sortOrder)).map((elem, index) => {
    const teamRanks = {};

    Object.keys(sortedTeamsRank).forEach((rankKey) => {
      teamRanks[rankKey] = getTeamRank(elem["Team"], sortedTeamsRank[rankKey]);
    });

    return {
      ...elem,
      ...teamRanks,
    };
  });

  return sortedAndRankedResponse;
};

const RegressionStandings = ({ user }) => {
  const { store } = useContext(AppContext);
  const [cookies, setCookie] = useCookies(["token_access, name"]);
  const [isDomLoaded, setIsDomLoaded] = useState(false);
  const [authToken, setAuthToken] = useState(cookies.token_access);
  const router = useRouter();
  let query = router.query;
  let leagueFromUrl = router.query?.league;
  let leagueName = router.query?.league;
  if (leagueFromUrl === "ncaa") {
    leagueFromUrl = "college_mens";
  }
  if (leagueFromUrl === "ncaaw") {
    leagueFromUrl = "college_womens";
  }
  const [year, setYear] = useState(getDefYear(leagueFromUrl));
  const yearArray = getYearArray(leagueFromUrl);
  const [coachData, setCoachData] = useState({
    confName: "",
    teams: [],
    isDisplayed: false,
  });
  const [rawTeamStandings, setRawTeamStandings] = useState([]);

  const [tableType, setTableType] = useState(() => {
    return query.tableType === "defensive" ? "regression_def" : "regression_off";
  });

  const [columnType, setColumnType] = useState(() => {
    return query.columnType ?? "keyDiff";
  });

  const [rankKey, setRankKey] = useState("keyDiffRank");

  const [mappedTableRawData, setMappedTableRawData] = useState([]);

  const [filteredTeamData, setFilteredTeamData] = useState([]);
  const [isDefaultSortLoaded, setIsDefaultSortLoaded] = useState(false);

  const tableRef = useRef(null);
  const [selectedTableData, setSelectedTableData] = useState([]);
  const [rawTableData, setRawTableData] = useState([]);
  const [splitType, setSplitType] = useState("All");

  const [sortOrder, sortedKey, sort] = useDynamicSort({
    tableData: filteredTeamData,
  });

  const windowSize = useWindowResize();
  const [slicedArray] = useInfiniteScroll({
    array: filteredTeamData,
    scrollType: windowSize?.["width"] > 768 ? "window" : "parent",
    parentRef: tableRef,
    itemsPerView: 35,
  });

  const tableAverage = useMemo(() => {
    if (selectedTableData.length > 0) {
      return {
        teamName: "",
        ...Utils.getMean(selectedTableData),
      };
    } else {
      return {};
    }
  }, [selectedTableData]);
  useEffect(() => {
    setIsDomLoaded(true);
    setAuthToken(document.cookie.split("token_access=").pop().split(";").shift());
    document.title = "ShotQuality | Team Standings";
  }, []);

  useEffect(() => {
    if (rawTableData?.data) {
      if (splitType === "All") {
        setSelectedTableData(rawTableData.data);
      } else if (splitType === "Away") {
        setSelectedTableData(rawTableData.away);
      } else if (splitType === "Home") {
        setSelectedTableData(rawTableData.home);
      }
    }
  }, [rawTableData, splitType]);

  useEffect(() => {
    if (authToken && leagueFromUrl) {
      authGetRequest(`/api/${leagueFromUrl}/${year}/team-standings`, authToken)
        .then((response) => {
          if (response.status !== 200) {
            let tempData = {
              data: [
                {
                  Actual_Record: "N/A",
                  Actual_Win_Percentage: 0.0,
                  Conference: NaN,
                  Defensive_Shot_Quality: 0.0,
                  Defensive_Shot_Quality_Rank_Visual: NaN,
                  Offensive_Shot_Quality: 0,
                  Offensive_Shot_Quality_Rank_Visual: NaN,
                  Record_Luck: 0.0,
                  SQ_Record: "N/A",
                  NET_SQ: 0,
                  SQ_Win_Percentage: 0.0,
                  Team_Name: NaN,
                  Adjusted_Shot_Quality: 0.0,
                },
              ],
            };
            return tempData;
          }
          return response.data;
        })
        .then((response) => {
          setRawTeamStandings(response);
        })
        .catch(async (err) => {
          if (err.message.includes("401")) {
            try {
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                body: authToken,
              })
                .then((res) => res.json())
                .then((response) => {
                  setCookie("token_access", response.access_token);
                  setAuthToken(response.access_token);
                });
            } catch (error) {
              console.log(error);
            }
          }
          console.log("Request failed :(");
        });
    }
  }, [authToken, leagueFromUrl, year]);

  useEffect(() => {
    if (authToken && leagueFromUrl) {
      authGetRequest(`/api/${leagueFromUrl}/${year}/${tableType}`, authToken)
        .then((response) => response.data)
        .then((response) => {
          setRawTableData(response);
          // setSelectedTableData(response.data);
        })
        .catch(async (err) => {
          if (err.message.includes("401")) {
            try {
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                body: authToken,
              })
                .then((res) => res.json())
                .then((response) => {
                  if (response.access_token) {
                    setCookie("token_access", response.access_token);
                    setAuthToken(response.access_token);
                  }
                });
            } catch (error) {
              console.error(error);
            }
          }
          console.error(err);
        });
    }
  }, [authToken, leagueFromUrl, year, tableType]);

  useEffect(() => {
    if (selectedTableData.length > 0 && rawTeamStandings?.data.length > 0) {
      const { data } = rawTeamStandings;
      const tableNameKey = tableType === "regression_off" ? "Logos" : "Opponent";

      let mappedConfData = selectedTableData.map((tableRow, index) => {
        return {
          ...tableRow,
          Conference: data.filter((dt) => dt["Team_Name"] === tableRow[tableNameKey])?.[0]?.["Conference"],
          _id: "regression_off" + index,

          Team: tableRow[tableNameKey],
        };
      });

      let rankedData = getDynamicRanks(mappedConfData, tableType);
      setFilteredTeamData(rankedData);
      setMappedTableRawData(rankedData);
    }
  }, [selectedTableData, rawTeamStandings]);

  useEffect(() => {
    setCoachData({
      confName: "",
      teams: [],
      isDisplayed: false,
    });

    if (!!store.isSignUpCoach && leagueFromUrl && mappedTableRawData.length > 0) {
      const tableNameKey = tableType === "regression_off" ? "Logos" : "Opponent";
      const schoolName = localStorage.getItem("school") ?? "";
      const confName = mappedTableRawData.filter(
        (team) => team?.[tableNameKey]?.toLowerCase() === schoolName.toLowerCase()
      )?.[0]?.["Conference"];

      if (confName) {
        const confOnlyTeams = mappedTableRawData.filter((team) => team["Conference"] === confName);

        if (confOnlyTeams.length > 0) {
          setCoachData({
            confName: confName,
            teams: getDynamicRanks(confOnlyTeams, tableType),
          });
        }
      }
    }
  }, [store, year, mappedTableRawData, tableType, leagueFromUrl]);

  const handleColumnTypeChange = (e) => {
    setRankKey(`${e.target.value}Rank`);
    setColumnType(e.target.value);
  };

  const handleTableTypeChange = (e) => {
    setTableType(e.target.value);
  };

  const handleCSVExportClick = (e) => {
    Utils.jsonToCsv(selectedTableData);
  };

  const handleSortClick = (key) => {
    let updatedKey;

    if (key === "Logos" || key === "Opponent") {
      updatedKey = key;
    } else {
      const filteredItem = regressionTableHeader.filter((elem) => elem["key"] === key) ?? [];

      if (filteredItem.length > 0) {
        updatedKey = filteredItem[0]?.[rankKey];
      }
    }

    let sortedData = sort(updatedKey);

    setFilteredTeamData([...sortedData]);
  };

  useEffect(() => {
    if (filteredTeamData?.length > 0 && !isDefaultSortLoaded && query.sortBy?.length > 0) {
      if (query?.columnType.length > 0) {
        setRankKey(`${query.columnType}Rank`);
      }

      const sortTimeout = setTimeout(() => {
        handleSortClick(query.sortBy);

        setIsDefaultSortLoaded(true);
        clearTimeout(sortTimeout);
      }, 100);
    }
  }, [isDefaultSortLoaded, filteredTeamData]);

  const searchConfig = {
    label: "Search by Team/Conference",
    tableData: filteredTeamData,
    setTableData: setFilteredTeamData,
    searchKeys: [
      {
        key: tableType === "regression_off" ? "Logos" : "Opponent",
        label: "Team",
      },
    ],
  };

  const toggleCoachConference = () => {
    if (coachData["isDisplayed"]) {
      setCoachData((coachData) => {
        return {
          ...coachData,
          isDisplayed: false,
        };
      });
      setFilteredTeamData(mappedTableRawData);
    } else {
      if (coachData["teams"].length > 0) {
        setCoachData((coachData) => {
          return {
            ...coachData,
            isDisplayed: true,
          };
        });
        setFilteredTeamData(coachData["teams"]);
      }
    }
  };

  const handleYearChange = (e) => {
    setSelectedTableData([]);
    setYear(e.target.value);
  };

  return (
    <>
      <Head>
        <title>{`${leagueName.toUpperCase()} Regression Stats | ShotQualityBets`}</title>
        <meta
          name="description"
          content={`${
            leagueName.toUpperCase() === "NCAAW" ? "NCAAW Women's" : leagueName.toUpperCase()
          } Regression Stats for your basketball picks, predictions, and analysis powered by the best data in basketball.`}
        />
      </Head>
      <MainLayout>
        <Content className="RegressionStandings">
          <div className="container-fluid">
            <div className="Titleside row">
              <div className="col-md-8 col-sm-12">
                <h2 className="header-title-txt">
                  {year} {leagueName.toUpperCase()} Regression Team Standings
                </h2>
              </div>
              {user && (
                <div className="col-md-4 col-sm-12">
                  <div
                    className="header-btn-wrapp justify-content-end d-flex"
                    style={{ gap: "1rem", flexWrap: "wrap" }}
                  >
                    {/* <FilterByCoachConf {...{ store, coachData, toggleCoachConference }} /> */}
                    <button className="btn btn-dark" onClick={handleCSVExportClick}>
                      Export to CSV
                    </button>
                  </div>
                </div>
              )}
              {/* {authToken && (
              )} */}
            </div>
          </div>
          {!user && (
            <div className="col-md-12 dummyLockWrapper">
              <div className="imgWrapper">
                <img src={SQBestBetsSreenshot.src} className="bracketBackground" alt="" />
              </div>

              <div
                className="dummyLock"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  position: "absolute",
                  top: "20px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <h3>Want to see more?</h3>
                <p>{SIGNUPS_CLOSED_MESSAGE}</p>
                <div className="requestEmailWrapper">
                  <div className="mm-email-wrapper">
                    <Form className="mm-form">
                      <Form.Group className="mb-3" controlId="formBasicEmail">
                        <InputGroup className="mb-3 mm-input-main-wrapper">
                          <Button
                            className={`mm-access-button mx-auto`}
                            style={{
                              backgroundColor: "#f26b0b",
                              color: "white",
                              padding: "10px 30px",
                              border: "none",
                            }}
                            onClick={() => router.push("/login")}
                          >
                            Sign In
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          )}
          {user && (
            <>
              <Content.HeaderSecondary>
                <div className="container-fluid">
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        flexWrap: "wrap",
                        gap: "2rem",
                        justifyContent: "space-between",
                        marginBottom: "1rem",
                      }}
                    >
                      <div className="paginationLinkGroup d-inline-flex">
                        {yearArray.map((yr) => (
                          <button
                            key={yr}
                            type="button"
                            className={`paginationLink ${year === yr ? "active" : ""}`}
                            onClick={handleYearChange}
                            value={yr}
                          >
                            {yr}
                          </button>
                        ))}
                      </div>
                      <div style={{ width: 350 }}>
                        {filteredTeamData.length > 0 ? <SearchInput {...searchConfig} /> : null}
                      </div>
                      <div className="text-center">
                        <div className="btn-group mr-1 mb-1">
                          <button
                            className={`btn btn-lg btn-primary ${splitType === "All" ? "active" : ""}`}
                            onClick={() => setSplitType("All")}
                            value="All"
                          >
                            All
                          </button>
                          <button
                            className={`btn btn-lg btn-primary ${splitType === "Away" ? "active" : ""}`}
                            onClick={() => setSplitType("Away")}
                            value="Away"
                          >
                            Away
                          </button>
                          <button
                            className={`btn btn-lg btn-primary ${splitType === "Home" ? "active" : ""}`}
                            onClick={() => setSplitType("Home")}
                            value="Home"
                          >
                            Home
                          </button>
                        </div>

                        <div className="btn-group mr-1 mb-1">
                          <button
                            className={`btn btn-lg btn-primary ${columnType === "keyDiff" ? "active" : ""}`}
                            onClick={handleColumnTypeChange}
                            value="keyDiff"
                          >
                            Difference
                          </button>
                          <button
                            className={`btn btn-lg btn-primary ${columnType === "keyFG" ? "active" : ""}`}
                            onClick={handleColumnTypeChange}
                            value="keyFG"
                          >
                            FG %
                          </button>
                          <button
                            className={`btn btn-lg btn-primary ${columnType === "keySQ" ? "active" : ""}`}
                            onClick={handleColumnTypeChange}
                            value="keySQ"
                          >
                            SQ FG %
                          </button>
                        </div>

                        <div className="btn-group mb-1">
                          <button
                            type="button"
                            className={`btn btn-lg btn-primary ${tableType === "regression_off" ? "active" : ""}`}
                            onClick={handleTableTypeChange}
                            value="regression_off"
                          >
                            Offensive
                          </button>
                          <button
                            type="button"
                            className={`btn btn-lg btn-primary ${tableType === "regression_def" ? "active" : ""}`}
                            onClick={handleTableTypeChange}
                            value="regression_def"
                          >
                            Defensive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {!selectedTableData.length ? <LoadingSkeleton height="70px" /> : null}
                </div>
              </Content.HeaderSecondary>

              <Content.Body>
                {isDomLoaded && (
                  <div className="container-fluid">
                    <ScrollableTable top="0px" ref={tableRef}>
                      {!selectedTableData.length ? (
                        <TableSkeleton
                          tableHeader={regressionTableHeader}
                          tableRowsCount={30}
                          className="table-space"
                          tableHeaderHeight="90px"
                        ></TableSkeleton>
                      ) : (
                        filteredTeamData.length > 0 && (
                          <div className="table-responsive responsive-to-card table-space">
                            <table className="table tl-fixed-desktop miscTeamStanding regresionTeamStanding ">
                              <thead>
                                <tr className="text-center">
                                  <TableHeader
                                    headers={regressionTableHeader}
                                    onSortClick={handleSortClick}
                                    ascOrder={sortOrder === "asc" ? true : false}
                                    sortedKey={sortedKey}
                                  />
                                </tr>
                                <tr className="text-center">
                                  {Object.keys(tableAverage).length > 0
                                    ? regressionTableHeader.map((elem, index) => {
                                        if (index === 0) {
                                          return (
                                            <th
                                              key={`table-header-2${index + 1}`}
                                              style={{
                                                background: "#D0D2D9",
                                                borderRadius: 0,
                                                color: "#0F131B",
                                              }}
                                              className={`regresion-table ${Utils.getTableHeaderStyle()} second-sticky-row `}
                                            >
                                              {tableAverage?.[elem[columnType]] ?? "Average"}
                                            </th>
                                          );
                                        }

                                        if (index === 1) {
                                          return (
                                            <th
                                              key={`table-header-2${index + 1}`}
                                              style={{
                                                background: "#D0D2D9",
                                                color: "#0F131B",
                                              }}
                                              className={`regresion-table  ${Utils.getTableHeaderStyle()} second-sticky-row`}
                                            ></th>
                                          );
                                        }
                                        if (index === 2) {
                                          return (
                                            <th
                                              key={`table-header-2${index + 1}`}
                                              style={{
                                                background: "#D0D2D9",
                                                color: "#0F131B",
                                              }}
                                              className={`regresion-table  ${Utils.getTableHeaderStyle()} second-sticky-row`}
                                            >
                                              {Utils.toPercent(tableAverage?.[elem[columnType]]) ?? ""}
                                            </th>
                                          );
                                        }
                                        return (
                                          <th
                                            key={`table-header-2${index + 1}`}
                                            style={{
                                              background: "#D0D2D9",
                                              borderRadius: 0,
                                              color: "#0F131B",
                                            }}
                                            className={`regresion-table  ${Utils.getTableHeaderStyle()} second-sticky-row`}
                                          >
                                            {Utils.toPercent(tableAverage?.[elem[columnType]])}
                                          </th>
                                        );
                                      })
                                    : null}
                                </tr>
                              </thead>
                              <tbody>
                                {slicedArray.map((data) => {
                                  const rankConfig = {
                                    columnType: tableType === "regression_def" ? "off" : "def",
                                    hideArrows: columnType === "keyDiff" ? false : true,
                                    leagueType: coachData["isDisplayed"] ? "conference" : leagueFromUrl,
                                    conferenceTeamCount:
                                      coachData["isDisplayed"] === true ? coachData["teams"].length : 0,
                                  };
                                  return (
                                    <tr key={data["_id"]}>
                                      <td style={{ textAlign: "center" }} data-name={"Team"} className="sm-w-100">
                                        <Link href={`/team/${leagueName}/${year}/${data["Logos"] || data["Opponent"]}`}>
                                          {data["Logos"] || data["Opponent"]}
                                        </Link>
                                      </td>
                                      <td style={{ textAlign: "center" }} data-name={"Conference"}>
                                        {/* <Link
                                        href={
                                          year === "2023"
                                            ? "#"
                                            : `/conference/${leagueName}/${year}/${data[regressionTableHeader[1].key]}`
                                        }
                                      > */}
                                        {data[regressionTableHeader[1].key]?.length > 13
                                          ? data[regressionTableHeader[1].key].slice(0, 12).concat("...")
                                          : data[regressionTableHeader[1].key]}
                                        {/* </Link> */}
                                      </td>

                                      <Fragment>
                                        <TableCellRegressionDiff
                                          dataName={regressionTableHeader[2].title}
                                          rankValue={data[regressionTableHeader[2][rankKey]]}
                                          {...rankConfig}
                                        >
                                          {Utils.toPercent(data[regressionTableHeader[2][columnType]])}
                                        </TableCellRegressionDiff>
                                        <TableCellRegressionDiff
                                          dataName={regressionTableHeader[3].title}
                                          rankValue={data[regressionTableHeader[3][rankKey]]}
                                          {...rankConfig}
                                        >
                                          {Utils.toPercent(data[regressionTableHeader[3][columnType]])}
                                        </TableCellRegressionDiff>
                                        <TableCellRegressionDiff
                                          dataName={regressionTableHeader[4].title}
                                          rankValue={data[regressionTableHeader[4][rankKey]]}
                                          {...rankConfig}
                                        >
                                          {Utils.toPercent(data[regressionTableHeader[4][columnType]])}
                                        </TableCellRegressionDiff>
                                        <TableCellRegressionDiff
                                          dataName={regressionTableHeader[5].title}
                                          rankValue={data[regressionTableHeader[5][rankKey]]}
                                          {...rankConfig}
                                        >
                                          {Utils.toPercent(data[regressionTableHeader[5][columnType]])}
                                        </TableCellRegressionDiff>
                                        <TableCellRegressionDiff
                                          dataName={regressionTableHeader[6].title}
                                          rankValue={data[regressionTableHeader[6]?.[rankKey]]}
                                          {...rankConfig}
                                        >
                                          {Utils.toPercent(data[regressionTableHeader[6][columnType]])}
                                        </TableCellRegressionDiff>
                                      </Fragment>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )
                      )}
                    </ScrollableTable>
                  </div>
                )}
              </Content.Body>
            </>
          )}
        </Content>
      </MainLayout>
    </>
  );
};

export default RegressionStandings;

export const getServerSideProps = appServerSideProps(async ({ appService }) => {
  const isUserLogged = appService.getUser();

  let user = null;

  if (isUserLogged) {
    user = await appService.get("api/account");
  }
  return { props: { user } };
});
